import React from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, RotateCcw, Send, Webhook, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch as BaseSwitch } from '@/components/ui/switch';
import { TelegramChatIdHelp } from '@/components/telegram-chat-id-help';
import { fetchInternalSettings, fetchTelegramWebhookInfo, putInternalSetting, sendTelegramTest, setupTelegramWebhook, } from '@/services/internal-setting';
import { isInternalAdmin } from '@/lib/roles';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { errorHandler } from '@/lib/utils';
import { td } from '@/lib/i18n';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/settings/')({
    component: SettingsPage,
});
function SettingsPage() {
    const { t } = useTranslation('internal');
    const { user } = useAuthenticationState();
    if (!isInternalAdmin(user)) {
        return <Navigate to="/console/internal"/>;
    }
    const { data: settings = [], isLoading } = useQuery({
        queryKey: ['internal-settings'],
        queryFn: fetchInternalSettings,
    });
    const groups = React.useMemo(() => groupBy(settings, (s) => s.group), [settings]);
    return (<div className="flex flex-col gap-6 p-6 max-w-3xl mx-auto w-full">
            <div className="space-y-1 border-b pb-4">
                <h1 className="text-3xl font-semibold">{t('settings.title')}</h1>
                <p className="text-sm text-muted-foreground">{t('settings.subtitle')}</p>
            </div>

            {isLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : (Object.entries(groups).map(([group, items]) => (<Card key={group}>
                        <CardHeader>
                            <CardTitle>{group}</CardTitle>
                            <CardDescription>{t('settings.count', { count: items.length })}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {items.map((setting) => (<SettingRow key={setting.key} setting={setting}/>))}
                            {group === 'Internal bot' && <TelegramWebhookSetupRow profile="internal"/>}
                            {group === 'Public bot' && <TelegramWebhookSetupRow profile="public"/>}
                        </CardContent>
                    </Card>)))}
        </div>);
}
function groupBy(rows, keyOf) {
    const out = {};
    for (const row of rows) {
        const k = keyOf(row);
        if (!out[k])
            out[k] = [];
        out[k].push(row);
    }
    return out;
}
function SettingRow({ setting }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const inputNameSuffix = React.useId();
    const [draft, setDraft] = React.useState(String(setting.value ?? ''));
    const [draftBool, setDraftBool] = React.useState(Boolean(setting.value));
    React.useEffect(() => {
        setDraft(String(setting.value ?? ''));
        setDraftBool(Boolean(setting.value));
    }, [setting.value]);
    const { mutate, isPending } = useMutation({
        mutationFn: putInternalSetting,
        onSuccess: () => {
            toast.success(t('settings.saved_key', { key: setting.key }));
            queryClient.invalidateQueries({ queryKey: ['internal-settings'] });
        },
        onError: errorHandler,
    });
    const { mutate: doTest, isPending: testing } = useMutation({
        mutationFn: sendTelegramTest,
        onSuccess: (data) => {
            if (data.sent) {
                toast.success(t('settings.test_telegram.success'));
            }
            else {
                toast.error(t('settings.test_telegram.failure', { error: data.error ?? 'unknown error' }));
            }
        },
        onError: errorHandler,
    });
    // Chat-ID rows always belong to the internal bot — the public bot delivers
    // into per-workspace / per-campaign chats configured outside settings.
    const isChatIdSetting = setting.key === 'driver.notifier.payout_chat_id' || setting.key === 'driver.notifier.photo_chat_id';
    const chatIdTestProfile = 'internal';
    const reset = () => {
        mutate({ key: setting.key, value: setting.default });
    };
    const isDirty = setting.type === 'bool'
        ? draftBool !== Boolean(setting.value)
        : draft !== String(setting.value ?? '');
    const save = () => {
        const value = setting.type === 'bool'
            ? draftBool
            : setting.type === 'int'
                ? parseInt(draft, 10)
                : draft;
        mutate({ key: setting.key, value });
    };
    const slug = setting.key.replace(/\./g, '_');
    const labelKey = `settings.label.${slug}`;
    const fallback = setting.key
        .split('.')
        .slice(-1)[0]
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    const friendlyLabel = td(t, labelKey, { defaultValue: fallback });
    // Secrets are rendered as `type="text"` with CSS-masked glyphs rather than
    // `type="password"`. Chrome's password manager fires the "save password?"
    // prompt on any password-typed input, regardless of `autocomplete` hints —
    // and these are bot tokens / webhook secrets, not user credentials. Using
    // `text` + `-webkit-text-security` keeps the visual mask without tripping
    // the heuristic. (Firefox falls back to plain text; acceptable for an
    // internal admin page.)
    const inputType = setting.type === 'int' ? 'number' : 'text';
    return (<div className="grid gap-2 md:grid-cols-[1fr_auto] md:items-end pb-4 border-b last:border-b-0 last:pb-0">
            <div className="space-y-1">
                <label htmlFor={setting.key} className="text-sm font-medium flex items-center gap-2">
                    {friendlyLabel}
                    {setting.isSecret && setting.hasValue && (<span className="text-[10px] uppercase tracking-wide text-emerald-500 inline-flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3"/>
                            {t('settings.configured')}
                        </span>)}
                    {isChatIdSetting && <TelegramChatIdHelp />}
                </label>
                <p className="text-xs text-muted-foreground">{setting.description}</p>
                {setting.value !== setting.default && !setting.isSecret && (<p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {t('common.default_value', { value: String(setting.default) })}
                    </p>)}
            </div>

            <div className="flex items-center gap-2">
                {setting.type === 'bool' ? (<BaseSwitch id={setting.key} checked={draftBool} onCheckedChange={setDraftBool} disabled={isPending}/>) : (<Input id={setting.key} type={inputType} value={draft} onChange={(e) => setDraft(e.target.value)} disabled={isPending} placeholder={setting.isSecret && setting.hasValue
                ? t('settings.secret_placeholder')
                : undefined} className={`w-48 ${setting.isSecret
                ? '[-webkit-text-security:disc] [text-security:disc] font-mono tracking-widest'
                : ''}`} name={`__internal_${slug}_${inputNameSuffix}`} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} data-1p-ignore="true" data-lpignore="true" data-form-type="other"/>)}

                <Button type="button" size="sm" onClick={save} disabled={isPending || !isDirty} title={t('settings.save')}>
                    {t('settings.save')}
                </Button>
                {isChatIdSetting && (<Button type="button" size="sm" variant="outline" onClick={() => doTest({ profile: chatIdTestProfile, chatId: draft })} disabled={testing || !draft} title={t('settings.test_telegram.title')}>
                        <Send className="h-3.5 w-3.5"/>
                        {t('settings.test_telegram.test_this_chat')}
                    </Button>)}
                {setting.value !== setting.default && !setting.isSecret && (<Button type="button" size="sm" variant="ghost" onClick={reset} disabled={isPending} title={t('settings.reset_to_default')}>
                        <RotateCcw className="h-3.5 w-3.5"/>
                    </Button>)}
            </div>
        </div>);
}
function TelegramWebhookSetupRow({ profile }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const { data: webhookInfo, refetch: refetchInfo } = useQuery({
        queryKey: ['telegram-webhook-info', profile],
        queryFn: () => fetchTelegramWebhookInfo(profile),
        // Don't error-toast — the bot may simply not be configured.
        retry: false,
    });
    const { mutate: doSetup, isPending: setupPending } = useMutation({
        mutationFn: () => setupTelegramWebhook(profile),
        onSuccess: (data) => {
            if (data.ok) {
                toast.success(t('settings.webhook.setup_success'));
                queryClient.invalidateQueries({ queryKey: ['internal-settings'] });
                refetchInfo();
            }
            else {
                toast.error(t('settings.webhook.setup_failure', { error: data.error ?? 'unknown error' }));
            }
        },
        onError: errorHandler,
    });
    const { mutate: doTest, isPending: testPending } = useMutation({
        mutationFn: () => sendTelegramTest({ profile }),
        onSuccess: (data) => {
            if (data.sent) {
                toast.success(t('settings.test_telegram.success'));
            }
            else {
                toast.error(t('settings.test_telegram.failure', { error: data.error ?? 'unknown error' }));
            }
        },
        onError: errorHandler,
    });
    const status = webhookInfo?.ok && webhookInfo.info?.url ? 'registered' : 'not_registered';
    const lastError = webhookInfo?.info?.last_error_message;
    const url = webhookInfo?.info?.url;
    return (<div className="border-t pt-4 space-y-3">
            <div>
                <p className="text-sm font-medium flex items-center gap-2">
                    <Webhook className="h-4 w-4"/>
                    {t('settings.webhook.title')}
                </p>
                <p className="text-xs text-muted-foreground">{t('settings.webhook.desc')}</p>
            </div>

            <div className="text-xs space-y-1">
                {status === 'registered' ? (<p className="flex items-center gap-1.5 text-emerald-500">
                        <CheckCircle2 className="h-3.5 w-3.5"/>
                        {t('settings.webhook.registered_at', { url })}
                    </p>) : (<p className="flex items-center gap-1.5 text-amber-500">
                        <XCircle className="h-3.5 w-3.5"/>
                        {t('settings.webhook.not_registered')}
                    </p>)}
                {lastError && (<p className="text-red-500">
                        {t('settings.webhook.last_error', { error: lastError })}
                    </p>)}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => doSetup()} disabled={setupPending}>
                    <Webhook className="h-3.5 w-3.5"/>
                    {status === 'registered'
            ? t('settings.webhook.reconfigure')
            : t('settings.webhook.configure')}
                </Button>
                {profile === 'internal' && (<Button type="button" size="sm" variant="outline" onClick={() => doTest()} disabled={testPending} title={t('settings.test_telegram.title')}>
                        <Send className="h-3.5 w-3.5"/>
                        {t('settings.test_telegram.title')}
                    </Button>)}
            </div>
        </div>);
}
